import { onMounted, ref } from 'vue'
import { useTelegram } from 'vue-tg'

const TEST_MODE = process.env.VUE_APP_TEST_MODE === 'true'
const TEST_USER_ID = process.env.VUE_APP_TEST_USER_ID || null

export function useTelegramData() {
  const userData = ref({
    firstName: '',
    lastName: '',
    photoUrl: '',
    username: '',
    id: null,
    languageCode: '',
    isPremium: false,
    isBot: false,
    authDate: null
  })

  const isLoading = ref(true)
  const isFullscreen = ref(false)
  let tg = null
  let telegram = null

  // Тестовый режим: подставляем user_id из .env и не обращаемся к Telegram
  if (TEST_MODE && TEST_USER_ID) {
    const parsedId = Number(TEST_USER_ID)
    const normalizedId = Number.isNaN(parsedId) ? TEST_USER_ID : parsedId

    userData.value = {
      ...userData.value,
      id: normalizedId,
      firstName: 'Test',
      lastName: 'User',
      username: 'test_user',
      authDate: new Date().toISOString()
    }

    isLoading.value = false

    return {
      userData,
      isLoading,
      isFullscreen,
      tg: null,
      telegram: null
    }
  }

  // Используем vue-tg для получения всех данных
  try {
    telegram = useTelegram()
    tg = telegram?.tg || null
  } catch (error) {
    console.warn('vue-tg not available:', error)
    // Fallback на прямую работу с Telegram WebApp
    if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
      tg = window.Telegram.WebApp
    }
  }

  onMounted(() => {
    // Если tg еще не получен, пробуем снова через vue-tg
    if (!tg && !telegram) {
      try {
        telegram = useTelegram()
        tg = telegram?.tg || null
      } catch (error) {
        if (typeof window !== 'undefined' && window.Telegram?.WebApp) {
          tg = window.Telegram.WebApp
        }
      }
    }

    // Получаем все доступные данные через vue-tg
    if (tg && tg.initDataUnsafe) {
      const initData = tg.initDataUnsafe
      const user = initData.user
      
      if (user) {
        // Получаем auth_date (дата авторизации в приложении, не дата создания аккаунта)
        let authDate = null
        if (initData.auth_date) {
          // auth_date это Unix timestamp в секундах
          authDate = new Date(initData.auth_date * 1000).toISOString()
        }
        
        // Получаем все доступные данные пользователя из Telegram Web App API
        const telegramUserData = {
          // Основные данные
          id: user.id || null,
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          username: user.username || '',
          languageCode: user.language_code || '',
          photoUrl: user.photo_url || '',
          isPremium: user.is_premium || false,
          isBot: user.is_bot || false,
          
          // Даты
          authDate: authDate // Дата авторизации в приложении
        }
        
        // Обновляем данные
        userData.value = telegramUserData
      }
    }
    
    isLoading.value = false

    // Проверяем режим открытия Mini App
    checkViewportMode()
    // Применяем отступ под хедер Telegram
    applySafeArea()
    // На десктопе мини-апп открывается named-app отдельным окном (t.me/bot/app),
    // где вьюпорт честный — специальные костыли ширины (is-tg-compact / фантом-режим /
    // requestFullscreen) больше НЕ нужны. Аппка ведёт себя как обычный адаптивный веб:
    // десктоп-грид включается @media (min-width:924px), иначе одноколоночная раскладка.
    // На всякий случай снимаем возможные устаревшие классы у залипших сессий.
    try {
      document.documentElement.classList.remove('is-tg-compact', 'is-desktop', 'is-tg-phantom', 'is-tg-offset')
    } catch (e) { /* noop */ }
  })

  // Функция для проверки режима открытия Mini App
  const checkViewportMode = () => {
    if (typeof window === 'undefined') return
    
    try {
      const telegramWebApp = window.Telegram?.WebApp
      if (!telegramWebApp) {
        // Если нет Telegram WebApp, считаем что не fullscreen
        isFullscreen.value = false
        return
      }

      // Полноэкранная компенсация (fullscreen-mode: position:fixed + safe-area padding)
      // нужна ТОЛЬКО на мобиле (iOS/Android), где Telegram рисует свою шапку поверх
      // мини-аппа. На Telegram Desktop / Web эвристика ниже ложно срабатывает
      // (viewportHeight ≈ innerHeight), а position:fixed привязывается к окну клиента,
      // а не к окну мини-аппа → контент выталкивается за видимую область (баг на ноутбуках).
      const platform = telegramWebApp.platform || ''
      if (platform !== 'android' && platform !== 'ios') {
        isFullscreen.value = false
        return
      }

      const viewportHeight = telegramWebApp.viewportHeight || 0
      const windowHeight = window.innerHeight || 0
      
      // В fullscreen режиме viewportHeight должен быть близок к windowHeight
      // В compact/fullsize режимах viewportHeight значительно меньше
      const heightDiff = Math.abs(windowHeight - viewportHeight)
      
      // Fullscreen: viewportHeight близок к windowHeight (разница < 150px)
      // и viewportHeight достаточно большой (> 400px для мобильных устройств)
      // Compact обычно имеет viewportHeight около 200-300px
      // Fullsize обычно имеет viewportHeight около 500-600px
      // Fullscreen имеет viewportHeight близкий к windowHeight
      isFullscreen.value = heightDiff < 150 && viewportHeight > 400 && windowHeight > 500
    } catch (error) {
      console.warn('Error checking viewport mode:', error)
      isFullscreen.value = false
    }
  }

  // Отступ сверху под телеграмовский хедер: safe area (вырез/статус-бар) + content safe area (шапка Telegram).
  // Пишем в CSS-переменную --app-safe-top; где перекрытия нет, инсеты = 0 → отступ 0.
  const applySafeArea = () => {
    if (typeof window === 'undefined') return
    try {
      const wa = window.Telegram?.WebApp
      const safeTop = (wa && wa.safeAreaInset && wa.safeAreaInset.top) || 0
      const contentTop = (wa && wa.contentSafeAreaInset && wa.contentSafeAreaInset.top) || 0
      document.documentElement.style.setProperty('--app-safe-top', (safeTop + contentTop) + 'px')
    } catch (error) {
      console.warn('Error applying safe area:', error)
    }
  }

  // Компакт (ноут): колонка прижата к левому-верхнему углу видимой панели (только CSS,
  // класс is-tg-compact). Центрирование НЕ используем — Telegram Desktop не отдаёт реальную
  // ширину видимой панели (все API + window.screenX недостоверны), любые попытки центрировать
  // ломаются в каком-то режиме окна. Здесь лишь снимаем возможные устаревшие классы.
  const applyPhantomMode = () => {
    if (typeof window === 'undefined') return
    try {
      document.documentElement.classList.remove('is-tg-offset', 'is-tg-phantom')
    } catch (error) { /* noop */ }
  }

  // Обработчики событий для обновления режима
  let resizeHandler = null
  let visibilityHandler = null

  // Инициализируем обработчики
  if (typeof window !== 'undefined') {
    // Открыта ли экранная клавиатура (в фокусе редактируемое поле).
    // На мобиле клавиатура резко уменьшает window.innerHeight → событие resize.
    const isKeyboardOpen = () => {
      const ae = document.activeElement
      if (!ae) return false
      const tag = ae.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || ae.isContentEditable === true
    }

    resizeHandler = () => {
      // Не пересчитываем режим вьюпорта, пока открыта клавиатура: сжатое окно
      // ложно переключает isFullscreen → корневой класс fullscreen-mode в App.vue
      // перещёлкивается и вся раскладка "перерендеривается" (баг при вводе адреса).
      if (!isKeyboardOpen()) {
        checkViewportMode()
      }
      applySafeArea()
      applyPhantomMode()
    }
    visibilityHandler = () => {
      if (!document.hidden) {
        setTimeout(checkViewportMode, 100)
        setTimeout(applySafeArea, 100)
      }
    }

    window.addEventListener('resize', resizeHandler)
    document.addEventListener('visibilitychange', visibilityHandler)

    // Подписка на изменения safe-area Telegram (Bot API 8.0+)
    const waEvents = window.Telegram?.WebApp
    if (waEvents && typeof waEvents.onEvent === 'function') {
      waEvents.onEvent('safeAreaChanged', applySafeArea)
      waEvents.onEvent('contentSafeAreaChanged', applySafeArea)
      waEvents.onEvent('viewportChanged', applySafeArea)
    }

    // Проверяем режим сразу
    setTimeout(checkViewportMode, 100)
    setTimeout(applySafeArea, 100)
  }

  return {
    userData,
    isLoading,
    isFullscreen,
    tg,
    telegram
  }
}
